import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Progress } from "@tabipla/ui";
import {
  ArrowRight,
  Check,
  Clock,
  Loader2,
  Wallet,
  PartyPopper,
  Banknote,
  Building2,
  Smartphone,
  ExternalLink,
  ChevronUp,
} from "lucide-react";
import type { Currency, SettlementWithPaymentDto } from "@tabipla/shared";
import { formatCurrency } from "@tabipla/shared";

// PayPay brand color
const PAYPAY_RED = "#FF0033";

export interface SettlementViewProps {
  /** Settlement data with payment status */
  settlements: SettlementWithPaymentDto[];
  /** Loading state */
  isLoading?: boolean;
  /** Current user ID */
  currentUserId: string;
  /** Current user type */
  currentUserType: "user" | "guest";
  /** Whether user can edit */
  canEdit: boolean;
  /** Callback to mark payment as paid */
  onMarkPaid: (settlement: SettlementWithPaymentDto) => Promise<void>;
  /** Whether mark paid is in progress */
  isMarkingPaid?: boolean;
  /** Callback to confirm receipt */
  onConfirmReceipt: (paymentId: string) => Promise<void>;
  /** Whether confirm is in progress */
  isConfirming?: boolean;
  /** Design variant: "avatar" (Waritto style) or "simple" (Tabitto style) */
  variant?: "avatar" | "simple";
  /** Show PayPay integration (Waritto only) */
  showPayPay?: boolean;
  /** Custom avatar renderer. If provided, replaces the default avatar in avatar variant. */
  renderAvatar?: (member: {
    name: string;
    memberId: string;
    memberType: string;
    avatarEmoji?: string | null;
    avatarColor?: string | null;
  }) => React.ReactNode;
}

function getInitials(name: string): string {
  const parts = name.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function getAvatarColor(name: string): string {
  const colors = [
    "bg-blue-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-cyan-500",
    "bg-teal-500",
    "bg-orange-500",
    "bg-amber-500",
  ];
  const hash = name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
}

/**
 * Sticky footer for quick payment action
 */
function StickyPaymentFooter({
  pendingPayments,
  onMarkPaid,
  isMarkingPaid,
  formatAmount,
  scrollToRef,
}: {
  pendingPayments: SettlementWithPaymentDto[];
  onMarkPaid: (settlement: SettlementWithPaymentDto) => Promise<void>;
  isMarkingPaid: boolean;
  formatAmount: (amount: number, currency: Currency) => string;
  scrollToRef: React.RefObject<HTMLDivElement | null>;
}) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(true);
  const [showOptions, setShowOptions] = useState(false);

  // Hide footer when there are no pending payments
  if (pendingPayments.length === 0) return null;

  // Group pending payments by currency for display
  const currencyGroups = pendingPayments.reduce(
    (acc, p) => {
      const currency = p.currency;
      if (!acc[currency]) {
        acc[currency] = { amount: 0, count: 0 };
      }
      acc[currency].amount += p.amount;
      acc[currency].count += 1;
      return acc;
    },
    {} as Record<Currency, { amount: number; count: number }>
  );
  const currencyList = Object.entries(currencyGroups) as [
    Currency,
    { amount: number; count: number },
  ][];

  // For single currency, use the total; for mixed, we'll show per-currency
  const hasMixedCurrencies = currencyList.length > 1;
  const totalAmount = hasMixedCurrencies
    ? 0
    : pendingPayments.reduce((sum, p) => sum + p.amount, 0);
  const defaultCurrency = pendingPayments[0]?.currency ?? "JPY";

  const handleScrollToCard = () => {
    if (scrollToRef.current) {
      scrollToRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleMarkPaid = async (settlement: SettlementWithPaymentDto) => {
    await onMarkPaid(settlement);
    setShowOptions(false);
  };

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg transition-all hover:bg-emerald-600"
        aria-label={t("payments.showPaymentAction", "支払いアクションを表示")}
      >
        <Wallet className="h-6 w-6" />
        <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold">
          {pendingPayments.length}
        </span>
      </button>
    );
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 border-t bg-white/95 px-4 py-3 shadow-lg backdrop-blur-sm dark:border-gray-700 dark:bg-gray-900/95 sm:inset-x-auto sm:bottom-4 sm:left-1/2 sm:max-w-md sm:-translate-x-1/2 sm:rounded-2xl sm:border">
      {/* Header with close button */}
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/50">
            <Wallet className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <p className="text-sm font-medium">
              {t("payments.yourPendingPayments", "あなたの支払い")}
            </p>
            <p className="text-xs text-muted-foreground">
              {hasMixedCurrencies
                ? t("payments.pendingPaymentCountMulti", "{{count}}件", {
                    count: pendingPayments.length,
                  })
                : t("payments.pendingPaymentCount", "{{count}}件 / 合計 {{amount}}", {
                    count: pendingPayments.length,
                    amount: formatAmount(totalAmount, defaultCurrency),
                  })}
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsVisible(false)}
          className="rounded-full p-1 text-muted-foreground hover:bg-gray-100 dark:hover:bg-gray-800"
          aria-label={t("common.minimize", "最小化")}
        >
          <ChevronUp className="h-5 w-5 rotate-180" />
        </button>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="flex-1" onClick={handleScrollToCard}>
          {t("payments.viewDetails", "詳細を見る")}
        </Button>
        {pendingPayments.length === 1 ? (
          <Button
            size="sm"
            className="flex-1 bg-emerald-500 hover:bg-emerald-600"
            onClick={() => handleMarkPaid(pendingPayments[0])}
            disabled={isMarkingPaid}
          >
            {isMarkingPaid ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-2 h-4 w-4" />
            )}
            {t("payments.markPaid", "払いました")}
          </Button>
        ) : (
          <Button
            size="sm"
            className="flex-1 bg-emerald-500 hover:bg-emerald-600"
            onClick={() => setShowOptions(!showOptions)}
          >
            <Check className="mr-2 h-4 w-4" />
            {t("payments.markPaid", "払いました")}
          </Button>
        )}
      </div>

      {/* Payment options dropdown */}
      {showOptions && pendingPayments.length > 1 && (
        <div className="mt-2 space-y-1 rounded-lg border bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-800/50">
          <p className="mb-1 text-xs text-muted-foreground">
            {t("payments.selectPayment", "どの支払いを完了しましたか？")}
          </p>
          {pendingPayments.map((payment) => {
            const key = `${payment.fromMemberType}-${payment.fromMemberId}-${payment.toMemberType}-${payment.toMemberId}`;
            return (
              <button
                key={key}
                onClick={() => handleMarkPaid(payment)}
                disabled={isMarkingPaid}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-white disabled:opacity-50 dark:hover:bg-gray-700"
              >
                <span>→ {payment.toMemberName}</span>
                <span className="font-semibold">
                  {formatAmount(payment.amount, payment.currency)}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Enhanced progress indicator for settlement completion
 */
function SettlementProgress({
  confirmedCount,
  paidCount,
  pendingCount,
  totalCount,
}: {
  confirmedCount: number;
  paidCount: number;
  pendingCount: number;
  totalCount: number;
}) {
  const { t } = useTranslation();
  const allConfirmed = totalCount > 0 && confirmedCount === totalCount;
  if (totalCount === 0 || allConfirmed) return null;

  const percentage = Math.round((confirmedCount / totalCount) * 100);

  return (
    <div className="mt-3 space-y-2">
      {/* Progress bar with percentage */}
      <div className="flex items-center gap-3">
        <Progress
          value={confirmedCount}
          max={totalCount}
          className="h-2.5 flex-1 bg-gray-100 dark:bg-gray-800"
        />
        <span className="min-w-[3rem] text-right text-sm font-medium text-emerald-600 dark:text-emerald-400">
          {percentage}%
        </span>
      </div>

      {/* Status breakdown */}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        {pendingCount > 0 && (
          <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
            <div className="h-2 w-2 rounded-full bg-gray-400" />
            {t("payments.pendingCount", "未払い {{count}}件", { count: pendingCount })}
          </div>
        )}
        {paidCount > 0 && (
          <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
            <div className="h-2 w-2 rounded-full bg-amber-500" />
            {t("payments.waitingCount", "確認待ち {{count}}件", { count: paidCount })}
          </div>
        )}
        {confirmedCount > 0 && (
          <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
            <div className="h-2 w-2 rounded-full bg-emerald-500" />
            {t("payments.confirmedCount", "完了 {{count}}件", { count: confirmedCount })}
          </div>
        )}
      </div>
    </div>
  );
}

export function SettlementView({
  settlements,
  isLoading = false,
  currentUserId,
  currentUserType,
  canEdit,
  onMarkPaid,
  isMarkingPaid = false,
  onConfirmReceipt,
  isConfirming = false,
  variant = "avatar",
  showPayPay = false,
  renderAvatar,
}: SettlementViewProps) {
  const { t } = useTranslation();
  const firstPendingRef = useRef<HTMLDivElement>(null);

  const formatAmount = (amount: number, currency: Currency = "JPY") => {
    return formatCurrency(amount, currency);
  };

  const isPayer = (settlement: SettlementWithPaymentDto) => {
    return (
      settlement.fromMemberId === currentUserId && settlement.fromMemberType === currentUserType
    );
  };

  // Calculate user's pending payments (payments the current user needs to make)
  const userPendingPayments = settlements.filter(
    (s) => canEdit && isPayer(s) && s.paymentStatus !== "paid" && s.paymentStatus !== "confirmed"
  );

  const isReceiver = (settlement: SettlementWithPaymentDto) => {
    return settlement.toMemberId === currentUserId && settlement.toMemberType === currentUserType;
  };

  const getStatusBadge = (settlement: SettlementWithPaymentDto) => {
    if (settlement.paymentStatus === "confirmed") {
      return (
        <Badge variant="default" className="bg-emerald-500 hover:bg-emerald-600">
          <Check className="mr-1 h-3 w-3" />
          {t("payments.statusConfirmed", "完了")}
        </Badge>
      );
    }
    if (settlement.paymentStatus === "paid") {
      return (
        <Badge
          variant="outline"
          className="border-amber-500 bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400"
        >
          <Clock className="mr-1 h-3 w-3" />
          {t("payments.statusWaiting", "確認待ち")}
        </Badge>
      );
    }
    // Pending (not paid yet)
    return (
      <Badge
        variant="outline"
        className="border-gray-400 bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
      >
        <Wallet className="mr-1 h-3 w-3" />
        {t("payments.statusPending", "未払い")}
      </Badge>
    );
  };

  const confirmedCount = settlements.filter((s) => s.paymentStatus === "confirmed").length;
  const paidCount = settlements.filter((s) => s.paymentStatus === "paid").length;
  const pendingCount = settlements.filter(
    (s) => s.paymentStatus !== "confirmed" && s.paymentStatus !== "paid"
  ).length;
  const totalCount = settlements.length;
  const allConfirmed = totalCount > 0 && confirmedCount === totalCount;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Simple variant (Tabitto style)
  if (variant === "simple") {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              {t("expenses.settlements")}
              {pendingCount > 0 && (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-sm font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                  {t("payments.remainingCount", "あと{{count}}件", {
                    count: pendingCount + paidCount,
                  })}
                </span>
              )}
            </CardTitle>
            {totalCount > 0 && (
              <span
                className={`text-sm ${allConfirmed ? "font-medium text-emerald-600" : "text-muted-foreground"}`}
              >
                {confirmedCount}/{totalCount} {t("payments.completed", "完了")}
              </span>
            )}
          </div>
          <SettlementProgress
            confirmedCount={confirmedCount}
            paidCount={paidCount}
            pendingCount={pendingCount}
            totalCount={totalCount}
          />
        </CardHeader>
        <CardContent>
          {settlements.length === 0 ? (
            <div className="flex flex-col items-center py-4 text-center">
              <Check className="mb-2 h-8 w-8 text-green-500" />
              <p className="text-sm text-muted-foreground">{t("expenses.settled")}</p>
            </div>
          ) : allConfirmed ? (
            <div className="flex flex-col items-center py-4 text-center">
              <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                <Check className="h-6 w-6 text-green-500" />
              </div>
              <p className="font-medium text-green-600 dark:text-green-400">
                {t("payments.allSettled", "すべての精算が完了しました")}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {settlements.map((settlement) => {
                const userIsPayer = isPayer(settlement);
                const userIsReceiver = isReceiver(settlement);
                const canMarkPaid =
                  canEdit &&
                  userIsPayer &&
                  settlement.paymentStatus !== "paid" &&
                  settlement.paymentStatus !== "confirmed";
                const canConfirm =
                  canEdit &&
                  userIsReceiver &&
                  settlement.paymentStatus === "paid" &&
                  settlement.paymentId;
                const isSettled = settlement.paymentStatus === "confirmed";

                const settlementKey = `${settlement.fromMemberType}-${settlement.fromMemberId}-${settlement.toMemberType}-${settlement.toMemberId}`;

                return (
                  <div
                    key={settlementKey}
                    className={`rounded-lg border p-3 transition-all ${
                      isSettled
                        ? "border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/30"
                        : settlement.paymentStatus === "paid"
                          ? "border-yellow-200 bg-yellow-50/50 dark:border-yellow-800 dark:bg-yellow-950/30"
                          : "bg-muted/50"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-medium ${userIsPayer ? "text-primary" : "text-red-600"}`}
                      >
                        {settlement.fromMemberName}
                        {userIsPayer && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            ({t("common.you", "あなた")})
                          </span>
                        )}
                      </span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span
                        className={`font-medium ${userIsReceiver ? "text-primary" : "text-green-600"}`}
                      >
                        {settlement.toMemberName}
                        {userIsReceiver && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            ({t("common.you", "あなた")})
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-xl font-bold">
                        {formatAmount(settlement.amount, settlement.currency)}
                      </span>
                      {getStatusBadge(settlement)}
                    </div>

                    {(canMarkPaid || canConfirm) && (
                      <div className="mt-3 flex justify-end gap-2 border-t pt-3">
                        {canMarkPaid && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 hover:bg-blue-50 hover:text-blue-700 dark:text-blue-400 dark:hover:bg-blue-950"
                            onClick={() => onMarkPaid(settlement)}
                            disabled={isMarkingPaid}
                          >
                            {isMarkingPaid ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Wallet className="mr-2 h-4 w-4" />
                            )}
                            {t("payments.markPaid", "払いました")}
                          </Button>
                        )}
                        {canConfirm && settlement.paymentId && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => onConfirmReceipt(settlement.paymentId as string)}
                            disabled={isConfirming}
                          >
                            {isConfirming ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="mr-2 h-4 w-4" />
                            )}
                            {t("payments.confirmReceipt", "受け取りました")}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Avatar variant (Waritto style)
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            {t("expenses.settlements")}
            {pendingCount > 0 && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-sm font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                {t("payments.remainingCount", "あと{{count}}件", {
                  count: pendingCount + paidCount,
                })}
              </span>
            )}
          </CardTitle>
          {totalCount > 0 && (
            <Badge
              variant="outline"
              className={
                allConfirmed
                  ? "border-emerald-500 bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50"
                  : "font-normal"
              }
            >
              {confirmedCount}/{totalCount} {t("payments.completed", "完了")}
            </Badge>
          )}
        </div>
        <SettlementProgress
          confirmedCount={confirmedCount}
          paidCount={paidCount}
          pendingCount={pendingCount}
          totalCount={totalCount}
        />
      </CardHeader>
      <CardContent>
        {settlements.length === 0 ? (
          <div className="flex flex-col items-center py-6 text-center">
            <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/50">
              <Check className="h-8 w-8 text-green-500" />
            </div>
            <p className="font-medium text-green-600 dark:text-green-400">
              {t("expenses.settled")}
            </p>
          </div>
        ) : allConfirmed ? (
          <div className="flex flex-col items-center py-6 text-center">
            <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-green-400 to-emerald-500">
              <PartyPopper className="h-8 w-8 text-white" />
            </div>
            <p className="text-lg font-semibold text-green-600 dark:text-green-400">
              {t("payments.allSettled", "すべての精算が完了しました")}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">お疲れさまでした！</p>
          </div>
        ) : (
          <div className="space-y-3">
            {settlements.map((settlement) => {
              const userIsPayer = isPayer(settlement);
              const userIsReceiver = isReceiver(settlement);
              const canMarkPaid =
                canEdit &&
                userIsPayer &&
                settlement.paymentStatus !== "paid" &&
                settlement.paymentStatus !== "confirmed";
              const canConfirm =
                canEdit &&
                userIsReceiver &&
                settlement.paymentStatus === "paid" &&
                settlement.paymentId;
              const isSettled = settlement.paymentStatus === "confirmed";
              const isPending = settlement.paymentStatus === "paid";

              const settlementKey = `${settlement.fromMemberType}-${settlement.fromMemberId}-${settlement.toMemberType}-${settlement.toMemberId}`;

              // Check if this is the first pending payment the user needs to make
              const isFirstUserPending =
                canMarkPaid &&
                userPendingPayments.length > 0 &&
                userPendingPayments[0].fromMemberId === settlement.fromMemberId &&
                userPendingPayments[0].toMemberId === settlement.toMemberId;

              return (
                <div
                  key={settlementKey}
                  ref={isFirstUserPending ? firstPendingRef : undefined}
                  className={`overflow-hidden rounded-xl border transition-all ${
                    isSettled
                      ? "border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 dark:border-green-800 dark:from-green-950/30 dark:to-emerald-950/30"
                      : isPending
                        ? "border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 dark:border-amber-800 dark:from-amber-950/30 dark:to-yellow-950/30"
                        : "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800/50"
                  }`}
                >
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {renderAvatar ? (
                          renderAvatar({
                            name: settlement.fromMemberName,
                            memberId: settlement.fromMemberId,
                            memberType: settlement.fromMemberType,
                            avatarEmoji: settlement.fromMemberAvatarEmoji,
                            avatarColor: settlement.fromMemberAvatarColor,
                          })
                        ) : settlement.fromMemberAvatarEmoji || settlement.fromMemberAvatarColor ? (
                          <div
                            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-lg ${settlement.fromMemberAvatarColor || "from-gray-300 to-gray-400"}`}
                          >
                            {settlement.fromMemberAvatarEmoji ||
                              getInitials(settlement.fromMemberName)}
                          </div>
                        ) : (
                          <div
                            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ${getAvatarColor(settlement.fromMemberName)}`}
                          >
                            {getInitials(settlement.fromMemberName)}
                          </div>
                        )}
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        {renderAvatar ? (
                          renderAvatar({
                            name: settlement.toMemberName,
                            memberId: settlement.toMemberId,
                            memberType: settlement.toMemberType,
                            avatarEmoji: settlement.toMemberAvatarEmoji,
                            avatarColor: settlement.toMemberAvatarColor,
                          })
                        ) : settlement.toMemberAvatarEmoji || settlement.toMemberAvatarColor ? (
                          <div
                            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-lg ${settlement.toMemberAvatarColor || "from-gray-300 to-gray-400"}`}
                          >
                            {settlement.toMemberAvatarEmoji || getInitials(settlement.toMemberName)}
                          </div>
                        ) : (
                          <div
                            className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ${getAvatarColor(settlement.toMemberName)}`}
                          >
                            {getInitials(settlement.toMemberName)}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold tabular-nums text-gray-900 dark:text-white">
                          {formatAmount(settlement.amount, settlement.currency)}
                        </p>
                        {getStatusBadge(settlement)}
                      </div>
                    </div>

                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <span className={userIsPayer ? "font-medium text-emerald-600" : ""}>
                        {settlement.fromMemberName}
                        {userIsPayer && (
                          <span className="ml-1 text-xs text-muted-foreground">(あなた)</span>
                        )}
                      </span>
                      <span className="text-muted-foreground">→</span>
                      <span className={userIsReceiver ? "font-medium text-emerald-600" : ""}>
                        {settlement.toMemberName}
                        {userIsReceiver && (
                          <span className="ml-1 text-xs text-muted-foreground">(あなた)</span>
                        )}
                      </span>
                    </div>
                  </div>

                  {canMarkPaid && showPayPay && (
                    <div className="border-t bg-gray-50/50 px-4 py-3 dark:bg-gray-900/50">
                      <p className="mb-2 text-xs text-muted-foreground">
                        {t(
                          "payments.paymentMethodHint",
                          "支払い方法を選んで送金後、「払いました」を押してください"
                        )}
                      </p>
                      <div className="mb-3 flex flex-wrap gap-2">
                        {settlement.toMemberPayPayUrl ? (
                          <button
                            type="button"
                            onClick={() => window.open(settlement.toMemberPayPayUrl!, "_blank")}
                            className="flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium text-white transition-opacity hover:opacity-90"
                            style={{ backgroundColor: PAYPAY_RED }}
                          >
                            <Smartphone className="h-3.5 w-3.5" />
                            PayPay
                            <ExternalLink className="h-3 w-3" />
                          </button>
                        ) : (
                          <div
                            className="group relative flex cursor-help items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                            title={t(
                              "payments.payPayUrlNotRegistered",
                              "{{name}}さんがPayPayのマイコードを登録すると使えます",
                              { name: settlement.toMemberName }
                            )}
                          >
                            <Smartphone className="h-3.5 w-3.5" />
                            PayPay
                            <span className="pointer-events-none absolute -top-8 left-1/2 z-10 hidden w-max max-w-[200px] -translate-x-1/2 rounded-lg bg-gray-900 px-2 py-1 text-center text-[10px] font-normal leading-tight text-white group-hover:block dark:bg-gray-700">
                              {t("payments.payPayUrlNotRegisteredShort", "{{name}}さんが未登録", {
                                name: settlement.toMemberName,
                              })}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                          <Building2 className="h-3.5 w-3.5" />
                          {t("payments.bankTransfer", "銀行振込")}
                        </div>
                        <div className="flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                          <Banknote className="h-3.5 w-3.5" />
                          {t("payments.cash", "現金")}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="w-full bg-emerald-500 text-white hover:bg-emerald-600"
                        onClick={() => onMarkPaid(settlement)}
                        disabled={isMarkingPaid}
                      >
                        {isMarkingPaid ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Wallet className="mr-2 h-4 w-4" />
                        )}
                        {t("payments.markPaid", "払いました")}
                      </Button>
                    </div>
                  )}

                  {canMarkPaid && !showPayPay && (
                    <div className="flex justify-end border-t bg-gray-50/50 px-4 py-3 dark:bg-gray-900/50">
                      <Button
                        size="sm"
                        className="w-full bg-emerald-500 text-white hover:bg-emerald-600"
                        onClick={() => onMarkPaid(settlement)}
                        disabled={isMarkingPaid}
                      >
                        {isMarkingPaid ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Wallet className="mr-2 h-4 w-4" />
                        )}
                        {t("payments.markPaid", "払いました")}
                      </Button>
                    </div>
                  )}

                  {canConfirm && settlement.paymentId && (
                    <div className="flex justify-end border-t bg-gray-50/50 px-4 py-3 dark:bg-gray-900/50">
                      <Button
                        size="sm"
                        className="w-full bg-emerald-500 text-white hover:bg-emerald-600"
                        onClick={() => onConfirmReceipt(settlement.paymentId as string)}
                        disabled={isConfirming}
                      >
                        {isConfirming ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="mr-2 h-4 w-4" />
                        )}
                        {t("payments.confirmReceipt", "受け取りました")}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Sticky footer for quick payment action */}
      {showPayPay && userPendingPayments.length > 0 && (
        <StickyPaymentFooter
          pendingPayments={userPendingPayments}
          onMarkPaid={onMarkPaid}
          isMarkingPaid={isMarkingPaid}
          formatAmount={formatAmount}
          scrollToRef={firstPendingRef}
        />
      )}
    </Card>
  );
}
